<script setup lang="ts">
import { routes } from '@app/router/routes'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

// Inline contact-support blocker that replaces a financial-action region
// (Add funds / Withdraw / Add bank / Invest) when the account is restricted.
// Parents render this conditionally — it does not self-gate — so the same
// widget drops into any action surface. Presentational only: navigation is a
// RouterLink (kept lint-clean inside `components/`), content stays browsable.
interface Props {
  title?: string
  description?: string
}

withDefaults(defineProps<Props>(), {
  title: 'Account access restricted',
  description:
    'This action is unavailable while your account access is restricted. Please contact support for more information.',
})
</script>

<template>
  <Box direction="col" align="center" gap="3" class="restriction-notice">
    <Box align="center" justify="center" class="restriction-notice__icon">
      <PiIcon name="lock" />
    </Box>
    <Box direction="col" gap="1" align="center">
      <Text type="subheading" weight="semibold" align="center">{{ title }}</Text>
      <Text type="body-sm" color="muted" align="center">{{ description }}</Text>
    </Box>
    <RouterLink :to="routes.HELP" class="restriction-notice__cta">
      <Text type="body-sm" weight="semibold" color="danger">Contact support</Text>
    </RouterLink>
  </Box>
</template>

<style scoped>
.restriction-notice {
  width: 100%;
  box-sizing: border-box;
  padding: 1.25rem;
  background: var(--p-danger-50);
  border: 1px solid var(--p-danger-200);
  border-radius: var(--p-border-radius-lg);
}

.restriction-notice__icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  font-size: 1rem;
  background: var(--p-danger-100);
  color: var(--p-danger-700);
}

.restriction-notice__cta {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border: 1px solid var(--p-danger-300);
  border-radius: var(--p-border-radius-md);
  text-decoration: none;
  background: var(--p-surface-0);
  transition: background 0.15s;
}

.restriction-notice__cta:hover {
  background: var(--p-danger-100);
}
</style>
